import React, { useState } from "react";
import { useFormState, useWatch } from "react-hook-form";

import { ToneSegments } from "../../../../components/FormPanel";
import {
  AmountCard,
  FormRow,
  SelectSheet,
  SelectSheetOption,
} from "../../../../components/MobileForm";
import {
  Card,
  CardLabel,
  RowsCard,
} from "../../../../components/MobileForm/styles";
import { FormInput, InputDate } from "../../../../components/Input";
import {
  AMOUNT_RULES,
  SEGMENT_LABELS,
  TONE_BY_TYPE,
  TYPE_OPTIONS,
} from "./constants";
import { MobileProgressFormProps } from "./types";

type AccountSheet = "from" | "to" | "on" | null;

/** Compact investment-progress fields: the amount hero with its type segments, plus account pickers driven by bottom sheets. */
export const MobileProgressForm: React.FC<MobileProgressFormProps> = ({
  control,
  register,
  setValue,
  clearErrors,
  transactionType,
  accountOptions,
  shouldShowFromAccount,
  shouldShowToAccount,
  shouldShowOnAccount,
  shouldShowNote,
  isTypeDisabled,
  isSubmitting,
  onTypeChange,
}) => {
  const [openSheet, setOpenSheet] = useState<AccountSheet>(null);

  const values = useWatch({ control });
  const { errors } = useFormState({ control });

  // The sheet-picked accounts have no Controller input, so register them here only to attach validation.
  if (shouldShowFromAccount) {
    register("fromAccount", { required: "From account is required" });
  }
  if (shouldShowToAccount || shouldShowOnAccount) {
    register("toAccount", {
      required: shouldShowOnAccount
        ? "On account is required"
        : "To account is required",
    });
  }

  const handleFromSelect = (option: SelectSheetOption) => {
    setValue("fromAccount", option, {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearErrors("fromAccount");
    setOpenSheet(null);
  };

  const handleToSelect = (option: SelectSheetOption) => {
    setValue("toAccount", option, { shouldDirty: true, shouldValidate: true });
    clearErrors("toAccount");
    setOpenSheet(null);
  };

  const showAccountRows =
    shouldShowFromAccount || shouldShowToAccount || shouldShowOnAccount;

  return (
    <>
      <AmountCard
        id="mobile-inv-progress-amount"
        label="Amount *"
        control={control}
        rules={AMOUNT_RULES}
        tone={TONE_BY_TYPE[transactionType.value]}
        disabled={isSubmitting}
        required
      >
        <ToneSegments
          groupLabel="Type"
          options={TYPE_OPTIONS}
          toneByValue={TONE_BY_TYPE}
          labelByValue={SEGMENT_LABELS}
          selectedValue={transactionType.value}
          disabled={isSubmitting || isTypeDisabled}
          onChange={onTypeChange}
        />
      </AmountCard>

      <Card>
        <CardLabel htmlFor="mobile-inv-progress-date">Date *</CardLabel>
        <InputDate
          id="mobile-inv-progress-date"
          name="transactionDate"
          control={control}
          rules={{ required: "Date is required" }}
          maxDate={new Date()}
          placeholder="YYYY/MM/DD"
          disabled={isSubmitting}
        />
      </Card>

      {showAccountRows && (
        <RowsCard>
          {shouldShowFromAccount && (
            <FormRow
              label="From account"
              value={values.fromAccount?.label}
              placeholder="Select account"
              error={errors.fromAccount?.message}
              onClick={() => setOpenSheet("from")}
            />
          )}
          {shouldShowToAccount && (
            <FormRow
              label="To account"
              value={values.toAccount?.label}
              placeholder="Select account"
              error={errors.toAccount?.message}
              onClick={() => setOpenSheet("to")}
            />
          )}
          {shouldShowOnAccount && (
            <FormRow
              label="On account"
              value={values.toAccount?.label}
              placeholder="Select account"
              error={errors.toAccount?.message}
              onClick={() => setOpenSheet("on")}
            />
          )}
        </RowsCard>
      )}

      {shouldShowNote && (
        <Card>
          <CardLabel htmlFor="mobile-inv-progress-note">Description</CardLabel>
          <FormInput
            id="mobile-inv-progress-note"
            name="note"
            control={control}
            rules={{
              minLength: {
                value: 3,
                message: "Description must be at least 3 characters",
              },
              maxLength: {
                value: 255,
                message: "Description cannot exceed 255 characters",
              },
            }}
            handleClear={false}
            disabled={isSubmitting}
            placeholder="Enter a description"
          />
        </Card>
      )}

      <SelectSheet
        isOpen={openSheet === "from"}
        title="Select from account"
        options={accountOptions}
        selectedValue={values.fromAccount?.value}
        onSelect={handleFromSelect}
        onClose={() => setOpenSheet(null)}
      />
      <SelectSheet
        isOpen={openSheet === "to"}
        title="Select to account"
        options={accountOptions}
        selectedValue={values.toAccount?.value}
        onSelect={handleToSelect}
        onClose={() => setOpenSheet(null)}
      />
      <SelectSheet
        isOpen={openSheet === "on"}
        title="Select account"
        options={accountOptions}
        selectedValue={values.toAccount?.value}
        onSelect={handleToSelect}
        onClose={() => setOpenSheet(null)}
      />
    </>
  );
};
